import { ButtonWrapper } from '@/components/form-builder/sections/section/styled';
import settings from '@/constants/constants';
import { IMMEDIATE_NURSE_CONSULT_TRACKING_ID, MAX_SMS_RETRY_COUNT } from '@/constants/immediateNurseConsultation';
import { GoogleAnalyticsEventName } from '@/constants/types';
import { useFormData } from '@/context/form';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import {
  ImmediateNurseConsultationVariant,
  useImmediateNurseConsultVariant,
} from '@/hooks/useImmediateNurseConsultVariant';
import ResendSmsVerificationDialog from '@/pages/components/resend-sms/ResendSmsVerificationDialog';
import { Alert, Button, Link, Typography } from '@montugroup/design-system';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneEnabledOutlinedIcon from '@mui/icons-material/PhoneEnabledOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { Box, Stack, styled } from '@mui/material';
import { lightBlue } from '@mui/material/colors';
import { useState } from 'react';

export type PatchPatientForInstantConsultationReply = {
  data: {
    retryAfterMs: number;
  };
};

const NurseConsultationHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(5),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const NurseConsultationSubHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(4),
  fontWeight: 'bold',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  fullWidth: true,
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const DefaultNurseConsultationInfo = () => (
  <Box minHeight="70vh">
    <PhoneEnabledOutlinedIcon color="secondary" sx={{ fontSize: '2rem', marginBottom: 4 }} />
    <Typography variant="h6" component="h6" fontWeight={'bold'} marginBottom={4}>
      Your initial assessment
    </Typography>
    <Typography marginBottom={4}>
      During this consultation you and a nurse will discuss your medical history and our services.
    </Typography>
    <Typography marginBottom={8}>Feel free to ask any questions you have.</Typography>
    <Typography marginBottom={4} fontWeight={'bold'}>
      What&apos;s next?
    </Typography>
    <Typography marginBottom={4}>
      If a nurse deems you eligible for the service, they&apos;ll organise your booking with a doctor, and you&apos;ll
      receive an email with next steps.
    </Typography>
  </Box>
);

const DefaultNurseConsultationButtons = ({
  showScheduleForLaterButton,
  handleAnalyticsOnClick,
  handleShowCalendar,
}: {
  showScheduleForLaterButton: boolean;
  handleAnalyticsOnClick: () => void;
  handleShowCalendar: () => void;
}) => (
  <>
    <Button
      variant="outlined"
      startIcon={<PhoneIphoneOutlinedIcon />}
      onClick={() => {
        handleAnalyticsOnClick();
        window.location.href = `tel:${settings.instantConnectPhoneNumber}`;
      }}
    >
      Disconnected? Call nurse again
    </Button>
    {showScheduleForLaterButton && (
      <>
        <Typography textAlign={'center'}>or</Typography>
        <Button
          variant="contained"
          startIcon={<CalendarTodayIcon />}
          color={'secondary'}
          fullWidth
          onClick={handleShowCalendar}
        >
          Schedule for later
        </Button>
      </>
    )}
  </>
);

const DefaultAssistanceInfo = () => (
  <Alert
    style={{ marginTop: 10 }}
    severity="info"
    icon={<QuizOutlinedIcon />}
    sx={{
      backgroundColor: lightBlue[50],
    }}
  >
    Need assistance? Visit our{' '}
    <Link
      href="https://support.alternaleaf.com.au/hc/en-au"
      rel="noopener noreferrer"
      target="_blank"
      sx={{
        fontWeight: 'bold',
      }}
    >
      help centre
    </Link>{' '}
    for answers to frequently asked questions or to find{' '}
    <Link
      href="https://support.alternaleaf.com.au/hc/en-au/articles/34017993658905-Contact-Alternaleaf"
      rel="noopener noreferrer"
      target="_blank"
      sx={{
        fontWeight: 'bold',
      }}
    >
      our contact details
    </Link>{' '}
    for further support.
  </Alert>
);

const AmazonConnectInitiatedNurseConsultationInfo = ({
  handleResendVerification,
}: {
  handleResendVerification: () => void;
}) => (
  <Stack minHeight="70vh" flexDirection={'column'} gap={4}>
    <PhoneEnabledOutlinedIcon color="secondary" sx={{ fontSize: '2rem' }} />
    <NurseConsultationHeading>Your initial assessment</NurseConsultationHeading>
    <Typography>
      Once you have verified your phone number, you will receive a call from Alternaleaf and be connected to one of our
      nurses for your consultation. <Link onClick={handleResendVerification}>Didn&apos;t receive SMS?</Link>
    </Typography>
    <Typography>
      During this consultation you and a nurse will discuss your medical history and our services. Feel free to ask any
      questions you have.
    </Typography>
    <NurseConsultationSubHeading>What&apos;s next?</NurseConsultationSubHeading>
    <Typography>
      If a nurse deems you eligible for the service, they&apos;ll organise your booking with a doctor, and you&apos;ll
      receive an email with next steps.
    </Typography>
    <NurseConsultationSubHeading>Disconnected?</NurseConsultationSubHeading>
    <Typography>
      Our nurse will attempt to re-call you to resume your consultation. In the event the consultation does not resume,
      you can call our support team or schedule your consult for later.
    </Typography>
  </Stack>
);

const AmazonConnectInitiatedNurseConsultationButtons = ({ handleShowCalendar }: { handleShowCalendar: () => void }) => (
  <>
    <StyledButton
      variant="contained"
      startIcon={<CalendarTodayIcon />}
      color={'secondary'}
      onClick={handleShowCalendar}
    >
      Schedule for later
    </StyledButton>
    <StyledButton
      variant="outlined"
      startIcon={<PhoneEnabledOutlinedIcon />}
      onClick={() => {
        window.location.href =
          'https://support.alternaleaf.com.au/hc/en-au/articles/34017993658905-Contact-Alternaleaf';
      }}
    >
      Contact support
    </StyledButton>
  </>
);

const AmazonConnectInitiatedAssistanceInfo = () => (
  <StyledAlert severity="info" icon={<QuizOutlinedIcon />}>
    Need assistance? Visit our{' '}
    <Link href="https://support.alternaleaf.com.au/hc/en-au" rel="noopener noreferrer" target="_blank">
      help centre
    </Link>{' '}
    for answers to frequently asked questions or for further support.
  </StyledAlert>
);

export const NurseConsultation = ({ handleShowCalendar }: { handleShowCalendar: () => void }) => {
  const [resendSmsVerificationDialogOpen, setResendSmsVerificationDialogOpen] = useState(false);
  const [_, setResendSmsVerificationError] = useState(false); // TODO: error state. pending with UX.

  const [sendSmsRetryCount, setSendSmsRetryCount] = useState(0);

  const { formData } = useFormData();

  const onSubmit = async (): Promise<PatchPatientForInstantConsultationReply> => {
    const userEmail =
      new URLSearchParams(window.location.search).get('email') || localStorage.getItem('prescreeningUserEmail');

    try {
      setResendSmsVerificationError(false);

      const response = await fetch(`${settings.xApiUrl}/forms/v1/patients/instant`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            type: 'patient',
            variant: 'variant-amazon-connect-initiated-inc',
            attributes: {
              firstName: formData?.firstName ?? '',
              lastName: formData?.lastName ?? '',
              phone: formData?.phone ?? '',
              email: userEmail ?? '',
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`'Failed to send SMS verification request due to an HTTP error: ': ${response.status}`);
      }

      const responseBody = await response.json();

      setSendSmsRetryCount((previousCount) => {
        if (previousCount > MAX_SMS_RETRY_COUNT) {
          return 0;
        }
        return previousCount + 1;
      });

      return responseBody;
    } catch (error) {
      console.error(
        'Failed to send SMS verification request:',
        error instanceof Error ? error.message : JSON.stringify(error),
      );

      setResendSmsVerificationError(true);

      return {
        data: {
          retryAfterMs: 0, // TODO: Error state pending with UX.
        },
      };
    }
  };

  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();
  const immediateConsultVariant = useImmediateNurseConsultVariant();

  const handleAnalyticsOnClick = () => {
    const storedTrackingPayload = localStorage.getItem(IMMEDIATE_NURSE_CONSULT_TRACKING_ID);
    if (storedTrackingPayload) {
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.START_BOOKING, JSON.parse(storedTrackingPayload));
    }
  };

  return immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC ? (
    <>
      <AmazonConnectInitiatedNurseConsultationInfo
        handleResendVerification={() => setResendSmsVerificationDialogOpen(true)}
      />
      <ButtonWrapper style={{ backgroundColor: 'white' }}>
        <AmazonConnectInitiatedNurseConsultationButtons handleShowCalendar={handleShowCalendar} />
        <AmazonConnectInitiatedAssistanceInfo />
      </ButtonWrapper>
      <ResendSmsVerificationDialog
        open={resendSmsVerificationDialogOpen}
        onClose={() => setResendSmsVerificationDialogOpen(false)}
        onSubmit={onSubmit}
        handleShowCalendar={handleShowCalendar}
        sendSmsRetryCount={sendSmsRetryCount}
        setSendSmsRetryCount={setSendSmsRetryCount}
      />
    </>
  ) : (
    <>
      <DefaultNurseConsultationInfo />
      <ButtonWrapper style={{ backgroundColor: 'white' }}>
        <DefaultNurseConsultationButtons
          showScheduleForLaterButton={immediateConsultVariant !== ImmediateNurseConsultationVariant.VARIANT_1}
          handleAnalyticsOnClick={handleAnalyticsOnClick}
          handleShowCalendar={handleShowCalendar}
        />
        <DefaultAssistanceInfo />
      </ButtonWrapper>
    </>
  );
};
